<template>
  <div
    class="d-flex flex-column h-100"
    style="gap: 0.5rem"
  >
    <div class="d-flex flex-column">
      <v-toolbar v-if="report">
        <v-btn
          v-b-tooltip
          class="mr-3"
          title="Back to Safety Home"
          @click="$router.push('/safety')"
          aria-label="Back to Safety Home"
        >
          <v-icon>mdi-arrow-up</v-icon>
        </v-btn>

        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          hide-details
          label="Search chemical by Name, CASRN or DTXSID"
          single-line
          style="max-width: 25em"
        />

        <v-spacer />
        <b-form-checkbox
          v-model="hideChemicalsWithNoSafety"
          switches
          title="Hide chemicals with no safety data"
          class="mx-3"
          v-b-tooltip
        >
          Hide Empty
        </b-form-checkbox>
        <b-form-checkbox
          v-model="showImages"
          switches
          title="Show/Hide chemical structure"
          class="mx-3"
          v-b-tooltip
        >
          Show Structure
        </b-form-checkbox>

        <b-form-select
          v-model="profile"
          v-b-tooltip
          :options="profiles"
          style="max-width: fit-content"
          title="Safety assessment profile"
        />

        <v-btn-toggle
          class="ml-3"
          dense
        >
          <ExportFilters
            :sections.sync="sections"
            :hierarchy="report.sections"
            :report="report.flags"
            button-title="mdi-file-export-outline"
            max-width="800"
            title="Export arbitrary section(s) in one of the supported formats"
          />
        </v-btn-toggle>

        <v-btn-toggle
          class="ml-3"
          dense
        >
          <OptionsDropdown
            :items="exportItems"
            title="Export to SDF, Excel, Word or PDF"
            :disabled="disableExport"
          />
          <ExportAllMenu
            :selected-structures="getCheckedStructures"
            v-model="downloading"
          />
        </v-btn-toggle>

        <v-btn-toggle
          class="ml-3"
          dense
        >
          <v-btn
            id="safety-report-select-all-button"
            aria-label="Select All"
            @click="onSelectAllStructures"
            :disabled="selectedStructures.length === filteredFlags.length"
          >
            <v-icon>mdi-checkbox-multiple-marked-outline</v-icon>
          </v-btn>

          <v-tooltip
            top
            v-bind="defaultVTooltipProps"
            activator="#safety-report-select-all-button"
          >
            Select All
          </v-tooltip>

          <v-btn
            id="safety-report-select-none-button"
            aria-label="Select None"
            @click="onClearSelection"
            :disabled="selectedStructures.length === 0"
          >
            <v-icon>mdi-checkbox-multiple-blank-outline</v-icon>
          </v-btn>

          <v-tooltip
            top
            v-bind="defaultVTooltipProps"
            activator="#safety-report-select-none-button"
          >
            Select none
          </v-tooltip>
        </v-btn-toggle>

        <v-btn-toggle
          class="ml-3"
          dense
        >
          <SaveDialog
            v-if="isAuthenticated"
            :api-url="`${SAFETY_URL}/reports`"
            :button-title="report.id ? 'mdi-content-save-edit-outline' : 'mdi-content-save-outline'"
            :item="report"
            :title="report.id ? 'Edit Report' : 'Save Report'"
            @itemCreated="onReportSaved"
            @itemUpdated="onReportUpdated"
          />

          <ACLDialog
            v-if="report.id && canACL(report)"
            :api-url="SAFETY_URL"
            :item="report"
            button-title="mdi-account-key"
            title="Access Control"
            type="reports"
            @aclUpdated="onAclUpdated"
          />

          <PromptDialog
            v-if="!report.id || canDelete(report)"
            :text="report.id ? 'Delete this Report?' : 'Do you want to clear Report?'"
            :title="report.id ? 'Delete Report' : 'Clear Report'"
            button-title="mdi-delete-outline"
            @confirmed="onReset"
          />
        </v-btn-toggle>

        <div style="padding-left: 12px">
          <TransferMenu
            :records="recordsForTransfer"
            only-icon
            hide-add-button
          />
        </div>
      </v-toolbar>

      <v-progress-linear
        v-if="downloading"
        indeterminate
      />
    </div>

    <v-data-table
      id="safety-table"
      v-if="report"
      :headers="headers"
      :items="filteredFlags"
      :footer-props="{
        showFirstLastPage: true,
        itemsPerPageOptions: [10, 30, 50, -1],
      }"
      :items-per-page="10"
      :hide-default-footer="report.flags.length < 10"
      :search="search"
      :custom-filter="filterChemical"
      class="h-100 d-flex flex-column"
      fixed-header
    >
      <template #[`item.chemical`]="{ item }">
        <ChemCell
          :chemical="item.chemical"
          :show-images="showImages"
          :show-checkbox="!!onStructureCheckboxClick"
          @check-click="onStructureCheckboxClick"
        />
      </template>

      <template #[`item.sah`]="{ item }">
        <a
          v-if="item.sah"
          href="javascript:void(0)"
          @click="onClickToReport(item.chemical)"
          title="Safety and Hazards data available"
          v-b-tooltip
        >
          <v-icon :size="iconSize">mdi-safety-goggles</v-icon>
        </a>
      </template>
      <template #[`item.exp`]="{ item }">
        <a
          v-if="item.exp"
          href="javascript:void(0)"
          @click="onClickToReport(item.chemical, 'Experimental Properties')"
          title="Experimental Properties data available"
          v-b-tooltip
        >
          <v-icon :size="iconSize">mdi-flask-empty-outline</v-icon>
        </a>
      </template>

      <template #[`item.flags.Signal`]="{ item }">
        <span
          :class="{
            'text-danger': item.flags.Signal === 'Danger',
            'text-info': item.flags.Signal === 'Warning',
          }"
        >
          {{ item.flags.Signal }}
        </span>
      </template>

      <template
        v-for="(h, i) in profileHeaders"
        #[`item.${h.value}`]="{ item, index }"
      >
        <span :key="i">
          <template v-if="h.type === 'ghs'">
            <template v-if="item.flags.GHS && item.flags.GHS.includes(h.keyword)">
              <v-menu
                :close-on-content-click="false"
                :open-delay="defaultVTooltipProps.openDelay"
                open-on-hover
                offset-x
                role="none"
              >
                <template #activator="{ on, attrs }">
                  <a
                    v-bind="attrs"
                    v-on="on"
                    href="javascript:void(0)"
                    :aria-label="h.keyword"
                    @click="onClickToReport(item.chemical, 'GHS Classification')"
                  >
                    <img
                      :id="`${h.value}-${index}`"
                      :src="`https://pubchem.ncbi.nlm.nih.gov/images/ghs/${h.keyword}.svg`"
                      :width="imgWidth"
                      :alt="h.keyword"
                    />
                  </a>
                </template>

                <PubchemInfoCard
                  :chemical="item.chemical"
                  section="GHS Classification"
                  :title-filter="titleFilter"
                />
              </v-menu>
            </template>
          </template>

          <template v-else-if="h.type === 'nfpa'">
            <template v-if="item.flags.NFPA && item.flags.NFPA.length">
              <v-menu
                :close-on-content-click="false"
                :open-delay="defaultVTooltipProps.openDelay"
                open-on-hover
                offset-x
                role="none"
              >
                <template #activator="{ on, attrs }">
                  <a
                    href="javascript:void(0)"
                    v-bind="attrs"
                    v-on="on"
                    aria-label="NFPA Hazard Classification"
                    @click="onClickToReport(item.chemical, 'NFPA Hazard Classification')"
                  >
                    <img
                      :id="`${h.value}-${index}`"
                      v-for="(n, j) in item.flags.NFPA"
                      :key="j"
                      :src="`https://pubchem.ncbi.nlm.nih.gov/image/nfpa.cgi?code=${n}`"
                      :width="imgWidth"
                      alt="NFPA hazard symbol"
                    />
                  </a>
                </template>

                <PubchemInfoCard
                  section="NFPA Hazard Classification"
                  :chemical="item.chemical"
                />
              </v-menu>
            </template>
          </template>

          <template v-else-if="item.flags[h.text]">
            <v-menu
              :close-on-content-click="false"
              :open-delay="defaultVTooltipProps.openDelay"
              open-on-hover
              offset-x
              role="none"
            >
              <template #activator="{ on, attrs }">
                <a
                  href="javascript:void(0)"
                  :aria-label="h.text"
                  @click="onClickToReport(item.chemical, h.text)"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon
                    :id="`${h.value}-${index}`"
                    :size="iconSize"
                  >
                    mdi-information-slab-circle-outline
                  </v-icon>
                </a>
              </template>

              <PubchemInfoCard
                :section="h.text"
                :chemical="item.chemical"
              />
            </v-menu>
          </template>
        </span>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { download, getDatedFilename, getErrorMessage, RESOLVER_URL, SAFETY_URL, isValidChemical } from '@/main'
import { showErrorToast } from '@/utils/toast'
import AuthBase from '@/components/auth/AuthBase'
import SaveDialog from '@/components/dialogs/SaveDialog.vue'
import ACLDialog from '@/components/auth/ACLDialog'
import PromptDialog from '@/components/dialogs/PromptDialog.vue'
import ChemCell from '@/components/chemical/ChemCell.vue'
import PubchemInfoCard from '@/components/pubchem/PubchemInfoCard.vue'
import ExportFilters from '@/components/safety/ExportFilters.vue'
import TransferMenu from '@/components/TransferMenu.vue'
import ExportAllMenu from '@/components/ExportAllMenu.vue'
import OptionsDropdown from '@/components/OptionsDropdown.vue'
import { cloneDeep, isEmpty } from 'lodash-es'
import { getFlags } from '../../store/safety'
import { defaultVTooltipProps } from '@/utils/defaultProps'
import { compareChemicals } from '@/utils/chemical'
import { safetyAppearancePreference } from '@/components/safety/safetyPreferences'

export default {
  mixins: [AuthBase],

  components: {
    ExportAllMenu,
    ExportFilters,
    ChemCell,
    PromptDialog,
    ACLDialog,
    SaveDialog,
    TransferMenu,
    OptionsDropdown,
    PubchemInfoCard,
  },

  data() {
    return {
      cts: null,
      downloading: false,
      report: null,
      titleFilter: ['Pictogram(s)', 'Signal', 'GHS Hazard Statements'],
      search: null,
      sections: [],
      selectedStructures: [],
      defaultVTooltipProps,
      imgWidth: 36,
      iconSize: 20,
    }
  },

  computed: {
    ...mapGetters([
      'feature',
      'getStructures',
      'getCheckedStructures',
      'safetyResult',
      'safetyProfiles',
      'safetyEndpoints',
      'isAuthenticated',
    ]),
    SAFETY_URL() {
      return SAFETY_URL
    },
    hideChemicalsWithNoSafety: {
      get() {
        return safetyAppearancePreference.value.hideND
      },
      set(value) {
        safetyAppearancePreference.value = {
          ...safetyAppearancePreference.value,
          hideND: Boolean(value),
        }
      },
    },
    showImages: {
      get() {
        return safetyAppearancePreference.value.showImages
      },
      set(value) {
        safetyAppearancePreference.value = {
          ...safetyAppearancePreference.value,
          showImages: Boolean(value),
        }
      },
    },
    profile: {
      get() {
        return safetyAppearancePreference.value.profile
      },
      set(value) {
        safetyAppearancePreference.value = {
          ...safetyAppearancePreference.value,
          profile: value ?? null,
        }
      },
    },
    reportId() {
      return this.$route.params.id
    },
    chemId() {
      if (this.$route.params.chemId) {
        return this.$route.params.chemId
      } else {
        return this.$route.query.ids
      }
    },
    profiles() {
      return [
        { value: null, text: 'Full', sortable: false },
        ...this.safetyProfiles.map((p) => ({ value: p.id, text: p.name })),
      ]
    },
    profileHeaders() {
      const selectedProfile = this.safetyProfiles.find((item) => item.id === this.profile)
      const endpoints = this.profile == null || !selectedProfile ? this.safetyEndpoints : selectedProfile.endpoints
      return endpoints.map((e) => ({
        value: e.id,
        text: e.name,
        type: e.type,
        keyword: e.keyword,
        sortable: false,
        class: 'rotated-header',
      }))
    },

    headers() {
      return [
        {
          value: 'chemical',
          text: 'Chemical',
          sort: (a, b) => compareChemicals(a, b),
        },
        { value: 'sah', text: 'Safety', class: 'rotated-header' },
        { value: 'exp', text: 'Properties', class: 'rotated-header' },
        { value: 'flags.Signal', text: 'Signal', class: 'rotated-header' },
        ...this.profileHeaders,
        ...(this.profile == null
          ? [
              {
                value: 'rqCode.category',
                text: 'RQ Category',
                class: 'rotated-header',
              },
              {
                value: 'rqCode.rq',
                text: 'RQ in pounds (kilograms)',
                class: 'rotated-header',
              },
            ]
          : []),
      ]
    },
    filteredFlags() {
      return this.report
        ? this.report.flags.filter((f) => !isEmpty(f.flags) || f.exp || f.sah || !this.hideChemicalsWithNoSafety)
        : []
    },
    recordsForTransfer() {
      return cloneDeep(this.selectedStructures)
    },
    disableExport() {
      return !this.report || this.selectedStructures.length === 0
    },
    getStructuresAsMap() {
      return new Map(this.getStructures.map((structure) => [structure.chemical?.sid, structure]))
    },
    exportItems() {
      return [
        { title: 'Export to SDF', icon: 'mdi-file-download-outline', onClick: () => this.onExport('SDF') },
        { title: 'Export to Excel', icon: 'mdi-file-excel-outline', onClick: () => this.onExport('XLSX') },
        { title: 'Export to Word', icon: 'mdi-file-word-outline', onClick: () => this.onExport('DOCX') },
        { title: 'Export to PDF', icon: 'mdi-file-pdf-box', onClick: () => this.onExport('PDF') },
      ]
    },
  },

  watch: {
    '$route.params': {
      handler() {
        location.reload()
      },
      immediate: false,
    },
  },

  mounted() {
    this.onSelectAllStructures()
  },

  methods: {
    row_class(item) {
      if (item.flags.Signal === 'Danger') return 'text-danger'
      else if (item.flags.Signal === 'Warning') return 'text-info'
    },
    onClickToReport(chemical, section) {
      if (section) this.$router.push(`/safety/reports/${chemical.sid}/${encodeURIComponent(section)}`)
      else this.$router.push(`/safety/reports/${chemical.sid}`)
    },
    onAclUpdated(acl) {
      this.report.acl = acl
    },
    onReset() {
      if (this.reportId) {
        this.$axios.delete(`${SAFETY_URL}/reports/${this.report.id}`).then(() => {
          if (this.report.id === this.safetyResult.id) this.$store.commit('resetSafetyResult')
        })
        this.$router.push('/safety/reports')
      } else {
        this.$store.commit('resetSafetyResult')
        this.$router.push('/safety')
      }
    },
    onExport(format) {
      this.downloading = true
      const selectedFlags = this.report.flags.filter((f) => this.selectedStructures.includes(f.chemical))
      selectedFlags.forEach((flag) => {
        flag.chemical.additionalProps = this.getStructuresAsMap.get(flag.chemical.sid).chemical.additionalProps
      })
      this.$axios
        .post(
          `${RESOLVER_URL}/export`,
          {
            flags: selectedFlags,
            outputFormat: format,
            showImages: this.showImages,
          },
          { responseType: 'blob' },
        )
        .then((response) => {
          let filename = getDatedFilename('safety', format.toLowerCase())
          download(filename, response.data, format.toLowerCase())
        })
        .catch((error) => {
          showErrorToast(error, { title: 'Error during export' })
        })
        .finally(() => {
          this.downloading = false
        })
    },
    onReportSaved(report) {
      report.flags.forEach((o) => {
        o.chemical.checked = false
      })
      this.report = cloneDeep(report)
      this.selectedStructures = []
      this.$router.push(`/safety/reports/${encodeURIComponent(report.id)}`)
    },
    onReportUpdated(report) {
      report.flags.forEach((o) => {
        o.chemical.checked = false
      })
      this.report = cloneDeep(report)
      this.selectedStructures = []
    },
    getData() {
      if (this.reportId) {
        this.$axios.get(`${SAFETY_URL}/reports/${this.reportId}`).then((response) => {
          response.data.flags.forEach((o) => {
            o.chemical.checked = false
          })
          this.report = cloneDeep(response.data)
        })
      }
    },
    filterChemical(value, search) {
      return (
        value &&
        ((value.sid && value.sid.toLowerCase().includes(search.toLowerCase())) ||
          (value.name && value.name.toLowerCase().includes(search.toLowerCase())) ||
          (value.casrn && value.casrn.toLowerCase().includes(search.toLowerCase())))
      )
    },
    onStructureCheckboxClick(structure) {
      const pos = this.selectedStructures.findIndex((item) => structure.chemId === item.chemId)

      if (pos !== -1) this.selectedStructures.splice(pos, 1)
      else this.selectedStructures.push(structure)
      structure.checked = pos == -1
    },

    onSelectAllStructures() {
      this.selectedStructures = []
      this.filteredFlags.forEach((o) => {
        o.chemical.checked = true
        this.selectedStructures.push(o.chemical)
      })
    },
    onClearSelection() {
      this.selectedStructures.forEach((o) => {
        o.checked = false
      })
      this.selectedStructures = []
    },
    buildReportByIds() {
      const searchIds = this.chemId.split(',')
      this.$axios
        .post(`${RESOLVER_URL}/lookup`, { ids: searchIds })
        .then((response) => {
          response.data.forEach((item) => {
            if (isValidChemical(item)) item.checked = true
          })
          this.found = response.data
          const chemicals = response.data.filter((item) => item.chemical).map((item) => item.chemical)
          if (chemicals.length > 0) {
            getFlags(chemicals, this)
          } else {
            this.report = null
            showErrorToast(response.data[0])
          }
        })
        .catch((error) => {
          showErrorToast(error)
        })
    },
  },
  created() {
    if (this.reportId) {
      this.getData()
    } else if (this.chemId) {
      this.buildReportByIds()
    } else {
      let t = this.safetyResult
      t.flags.forEach((o) => {
        o.chemical.checked = false
      })
      this.report = cloneDeep(t)
    }
  },
}
</script>

<style scoped lang="scss">
::v-deep #safety-table.v-data-table thead {
  line-height: 1;
  height: 7rem;
  text-align: center !important;
}

::v-deep #safety-table.v-data-table th {
  text-align: center !important;
}

::v-deep #safety-table.v-data-table th.rotated-header {
  writing-mode: sideways-lr;
  padding-left: 0.2rem;
  padding-right: 0.2rem;
  overflow: hidden;
  text-overflow: ellipsis;
}

::v-deep #safety-table .v-data-table__wrapper {
  display: flex;
  flex-direction: column;
  overflow: auto;
  flex-basis: 0 !important;
  flex-grow: 1 !important;
}

::v-deep #safety-table.v-data-table td {
  padding: 5px;
  text-align: center !important;
  // vertical-align: middle !important;
}
</style>
